import { Loader2Icon, XIcon } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { EditorConfig } from '@/services/api/onlyofficeService';

interface OnlyOfficeEditorProps {
  editorConfig: EditorConfig;
  onClose: () => void;
  title?: string;
}

export const OnlyOfficeEditor: React.FC<OnlyOfficeEditorProps> = ({
  editorConfig,
  onClose,
  title,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const editorInstanceRef = useRef<unknown>(null);

  const initEditor = useCallback(() => {
    const {DocsAPI} = (window as unknown as Record<string, unknown>);
    if (!DocsAPI || !containerRef.current) return false;

    try {
      // Deep-clone to strip Immer/RTK Query proxy wrappers.  OnlyOffice's
      // api.js serialises the config via postMessage to its iframe; Immer
      // proxies can cause the token (and other nested values) to arrive as
      // `undefined` on the other side.
      const configWithSize = {
        ...JSON.parse(JSON.stringify(editorConfig.config)),
        height: '100%',
        width: '100%',
      };
      const api = DocsAPI as { DocEditor: new (id: string, config: Record<string, unknown>) => unknown };
      editorInstanceRef.current = new api.DocEditor(
        'onlyoffice-editor-container',
        configWithSize,
      );
      setLoading(false);
      return true;
    } catch (err) {
      setError(`Failed to initialize editor: ${err}`);
      setLoading(false);
      return false;
    }
  }, [editorConfig]);

  useEffect(() => {
    let isMounted = true;

    const scriptId = 'onlyoffice-api-script';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;

    const tryInit = () => {
      if (!isMounted) return;
      if (!initEditor()) {
        // Retry once after a short delay
        setTimeout(() => {
          if (!isMounted) return;
          if (!initEditor()) {
            setError(
              `OnlyOffice Document Server not available at ${editorConfig.document_server_url}`,
            );
            setLoading(false);
          }
        }, 1000);
      }
    };

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = `${editorConfig.document_server_url}/web-apps/apps/api/documents/api.js`;
      script.async = true;
      script.onload = () => tryInit();
      script.onerror = () => {
        if (!isMounted) return;
        setError(
          `Failed to load OnlyOffice API from ${editorConfig.document_server_url}`,
        );
        setLoading(false);
      };
      document.body.appendChild(script);
    } else {
      // Script already loaded, try to init after a brief delay
      setTimeout(tryInit, 300);
    }

    return () => {
      isMounted = false;
      // Properly destroy the editor instance so the SDK releases its
      // internal Socket.IO connection and session state.  Without this,
      // reopening the editor reuses the old (possibly pre-JWT) session
      // and the Document Server receives `token = undefined`.
      if (editorInstanceRef.current) {
        try {
          (editorInstanceRef.current as { destroyEditor?: () => void }).destroyEditor?.();
        } catch {
          // Editor may already be disposed
        }
      }
      editorInstanceRef.current = null;
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      // Remove the script tag so the SDK is fully reloaded on next mount,
      // preventing stale global DocsAPI state from interfering.
      const oldScript = document.getElementById('onlyoffice-api-script');
      if (oldScript) {
        oldScript.remove();
      }
    };
  }, [editorConfig, initEditor]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-zinc-900">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50">
        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300 truncate">
          {title || editorConfig.config.document.title}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-200 dark:border-zinc-700 text-xs font-medium hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
        >
          <XIcon className="h-3.5 w-3.5" />
          Close
        </button>
      </div>

      {/* Editor area */}
      <div className="flex-1 relative">
        {error ? (
          <div className="flex items-center justify-center h-full p-4">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        ) : (
          <>
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <div className="flex flex-col items-center gap-2">
                  <Loader2Icon className="h-6 w-6 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">
                    Loading editor...
                  </span>
                </div>
              </div>
            )}
            <div
              ref={containerRef}
              id="onlyoffice-editor-container"
              className="w-full h-full"
              style={{ display: loading ? 'none' : 'block' }}
            />
          </>
        )}
      </div>
    </div>
  );
};
